import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  ChevronLeft,
  ChevronRight,
  MapPinned,
  Trophy,
} from "lucide-react";
import { Button } from "../components/Button";
import {
  InteractiveLakeMap,
  type LakeMapSwim,
} from "../components/InteractiveLakeMap";
import { formatDateTime } from "../lib/format";

type ReplayEvent = {
  id: string;
  kind: string;
  at: string;
  title: string;
  detail: string;
  rod_number?: number | null;
  peg_name?: string | null;
  importance?: "major" | "normal" | "context";
};

function clock(value: string) {
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(value));
}

function durationLabel(minutes: number | null | undefined) {
  if (minutes == null || !Number.isFinite(Number(minutes))) return "Not recorded";
  const mins = Number(minutes);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const remaining = mins % 60;
  return remaining ? `${hours}h ${remaining}m` : `${hours}h`;
}

function eventTone(event: ReplayEvent, selected: boolean) {
  if (selected) return "border-accent bg-accent/5";
  if (event.importance === "major") return "border-border bg-raised";
  return "border-border/70 bg-background";
}

export default function SessionReplayPage() {
  const { id } = useParams();
  const [selectedIndex, setSelectedIndex] = useState(0);

  const replayQuery = useQuery({
    queryKey: ["catchcompass_session_replay", id],
    queryFn: () =>
      fetch(`/app-api/sessions/${id}/replay`).then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Replay not available");
        return data;
      }),
    enabled: Boolean(id),
  });

  const lakeId = replayQuery.data?.session?.lake_id;
  const mapQuery = useQuery({
    queryKey: ["catchcompass_lake_map", lakeId],
    queryFn: () =>
      fetch(`/app-api/lakes/${lakeId}/map`).then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Map not available");
        return data;
      }),
    enabled: Boolean(lakeId),
  });

  const timeline = (replayQuery.data?.timeline ?? []) as ReplayEvent[];

  useEffect(() => {
    if (!timeline.length) {
      setSelectedIndex(0);
      return;
    }
    setSelectedIndex((start) => Math.min(start, timeline.length - 1));
  }, [timeline.length]);

  const activePeg = useMemo(() => {
    let peg = replayQuery.data?.session?.peg || null;
    for (let index = 0; index <= selectedIndex && index < timeline.length; index += 1) {
      if (timeline[index]?.peg_name) peg = timeline[index].peg_name;
    }
    return peg;
  }, [replayQuery.data?.session?.peg, selectedIndex, timeline]);

  if (replayQuery.isLoading) return <PageMessage text="Building the session replay…" />;
  if (replayQuery.error || !replayQuery.data?.session)
    return <PageMessage text="This session replay could not be loaded." link="/sessions" />;

  const data = replayQuery.data;
  const session = data.session;
  const summary = data.summary;
  const analysis = data.analysis ?? {};
  const selected = timeline[selectedIndex] ?? null;
  const mapData = mapQuery.data?.lake ? mapQuery.data : null;
  const mapSwims = (mapData?.swims ?? []) as LakeMapSwim[];

  return (
    <div className="mx-auto max-w-[1040px] space-y-6 px-4 py-6 pb-28 md:py-8 md:pb-10">
      <header>
        <Link
          to={`/sessions/${session.id}`}
          className="text-sm font-medium text-accent hover:underline"
        >
          Back to session
        </Link>
        <div className="mt-3 flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-accent">
              CatchCompass Match of the Day
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-primary">
              Session replay
            </h1>
            <p className="mt-1 text-sm text-secondary">
              {session.lake_name}
              {session.peg ? ` · ${session.peg}` : ""} · {formatDateTime(session.start_at)}
            </p>
          </div>
          <Button asChild variant="secondary">
            <Link to={`/lakes/${session.lake_id}/map${activePeg ? `?peg=${encodeURIComponent(activePeg)}` : ""}`}>
              <MapPinned className="size-4" /> Full lake map
            </Link>
          </Button>
        </div>
      </header>

      {!session.is_complete ? (
        <section className="rounded-xl border border-accent/40 bg-accent/5 p-4">
          <p className="text-sm text-secondary">
            This is a live replay. The final verdict will keep changing until the session ends.
          </p>
        </section>
      ) : null}

      <section className="rounded-2xl border border-border bg-raised p-5">
        <div className="flex items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-inset text-accent">
            <Trophy className="size-5" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-secondary">
              Full-time summary
            </p>
            <p className="mt-1 text-lg font-semibold leading-7 text-primary">
              {summary.headline}
            </p>
            <p className="mt-1 text-sm text-secondary">
              {durationLabel(summary.duration_minutes)} · {summary.peg_moves} peg move
              {summary.peg_moves === 1 ? "" : "s"} · {summary.observations} swim observation
              {summary.observations === 1 ? "" : "s"}
            </p>
          </div>
        </div>
      </section>

      <section className="grid gap-3 sm:grid-cols-3">
        <Metric label="Landed fish" value={summary.catches} />
        <Metric label="Meaningful indications" value={summary.meaningful_events} />
        <Metric label="Session length" value={durationLabel(summary.duration_minutes)} />
      </section>

      {analysis.turning_point ? (
        <section className="rounded-xl border border-accent/50 bg-accent/5 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-accent">
            Turning point
          </p>
          <p className="mt-2 text-sm leading-6 text-primary">{analysis.turning_point}</p>
        </section>
      ) : null}

      <section className="grid gap-3 md:grid-cols-2">
        <AnalysisCard
          title="What worked"
          items={analysis.what_worked}
          empty="No clear productive pattern from this single session."
        />
        <AnalysisCard
          title="What didn't produce"
          items={analysis.what_didnt_produce}
          empty="Nothing deserves a negative label from the recorded exposure."
        />
        <AnalysisCard
          title="Conditions changed"
          items={analysis.conditions}
          empty="No material change stood out across the stored weather checkpoints."
        />
        <AnalysisCard
          title="What CatchCompass learned"
          items={analysis.learning}
          empty="This session still contributes exposure evidence."
        />
      </section>

      <section className="space-y-4 rounded-2xl border border-border bg-raised p-4 sm:p-5">
        <div className="flex items-center gap-2">
          <Activity className="size-5 text-accent" />
          <div>
            <h2 className="font-semibold text-primary">Replay the session</h2>
            <p className="text-sm text-secondary">
              Move through the timeline and the map follows the peg being fished.
            </p>
          </div>
        </div>

        {mapData ? (
          <InteractiveLakeMap
            latitude={mapData.lake.latitude}
            longitude={mapData.lake.longitude}
            swims={mapSwims}
            highlightName={activePeg}
            compact
          />
        ) : (
          <div className="rounded-xl border border-dashed border-border p-5 text-sm text-secondary">
            The timeline is available, but this lake does not have enough map data yet.
          </div>
        )}

        {selected ? (
          <div className={`rounded-xl border p-4 ${eventTone(selected, true)}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-accent">
                  {clock(selected.at)}
                  {selected.peg_name ? ` · ${selected.peg_name}` : ""}
                </p>
                <h3 className="mt-1 text-lg font-semibold text-primary">{selected.title}</h3>
                <p className="mt-1 text-sm leading-6 text-secondary">{selected.detail}</p>
              </div>
              <span className="shrink-0 rounded-full bg-inset px-2 py-1 text-xs text-secondary">
                {selectedIndex + 1}/{timeline.length}
              </span>
            </div>
          </div>
        ) : null}

        {timeline.length > 1 ? (
          <div className="space-y-3">
            <input
              type="range"
              min={0}
              max={timeline.length - 1}
              value={selectedIndex}
              onChange={(event) => setSelectedIndex(Number(event.target.value))}
              className="w-full accent-current"
              aria-label="Session replay position"
            />
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={selectedIndex <= 0}
                onClick={() => setSelectedIndex((index) => Math.max(0, index - 1))}
              >
                <ChevronLeft className="size-4" /> Previous
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={selectedIndex >= timeline.length - 1}
                onClick={() =>
                  setSelectedIndex((index) => Math.min(timeline.length - 1, index + 1))
                }
              >
                Next <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>
        ) : null}
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-primary">The match timeline</h2>
          <p className="mt-1 text-sm text-secondary">
            Weather, observations, decisions, rod events and catches in the order they happened.
          </p>
        </div>
        <div className="space-y-2">
          {timeline.map((event, index) => (
            <button
              key={event.id}
              type="button"
              onClick={() => setSelectedIndex(index)}
              className={`w-full rounded-xl border p-3 text-left transition-colors ${eventTone(
                event,
                index === selectedIndex,
              )}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-12 shrink-0 text-xs font-semibold text-secondary">
                  {clock(event.at)}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-primary">{event.title}</p>
                  <p className="mt-0.5 text-sm text-secondary">{event.detail}</p>
                  {event.peg_name ? (
                    <p className="mt-1 text-xs text-tertiary">{event.peg_name}</p>
                  ) : null}
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {Array.isArray(analysis.peg_exposure) && analysis.peg_exposure.length ? (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-primary">Peg exposure</h2>
          <div className="grid gap-2 sm:grid-cols-2">
            {analysis.peg_exposure.map((row: any, index: number) => (
              <div
                key={`${row.peg}-${index}`}
                className="rounded-xl border border-border bg-raised p-3"
              >
                <p className="font-semibold text-primary">{row.peg}</p>
                <p className="mt-1 text-sm text-secondary">
                  {durationLabel(row.duration_minutes)} · {row.catches} landed fish
                </p>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {data.session_report ? (
        <section className="rounded-xl border border-border bg-inset p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-secondary">
            Saved session report
          </p>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-secondary">
            {data.session_report}
          </p>
        </section>
      ) : null}

      <p className="text-xs leading-5 text-tertiary">
        Replay findings describe the evidence recorded in this session. Sequences such as a move followed by a catch are useful evidence, but CatchCompass does not treat them as proof of causation.
      </p>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-raised p-4">
      <p className="text-xl font-semibold text-primary">{value}</p>
      <p className="mt-1 text-xs text-secondary">{label}</p>
    </div>
  );
}

function AnalysisCard({
  title,
  items,
  empty,
}: {
  title: string;
  items?: string[];
  empty: string;
}) {
  const safeItems = Array.isArray(items) ? items.filter(Boolean) : [];
  return (
    <div className="rounded-xl border border-border bg-raised p-4">
      <h2 className="font-semibold text-primary">{title}</h2>
      {safeItems.length ? (
        <ul className="mt-2 space-y-2 text-sm leading-6 text-secondary">
          {safeItems.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-sm leading-6 text-secondary">{empty}</p>
      )}
    </div>
  );
}

function PageMessage({ text, link }: { text: string; link?: string }) {
  return (
    <div className="mx-auto max-w-[760px] px-4 py-10 text-center">
      <p className="text-sm text-secondary">{text}</p>
       {link ? (
        <Link
          to={link}
          className="mt-3 inline-block text-sm font-medium text-accent hover:underline"
        >
          Back
        </Link>
      ) : null}
    </div>
  );
}
